import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Tooltip, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { keyframes } from '@emotion/react';

const pulseEffect = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
`;

const OnlineIndicator = ({ 
  userId, 
  size = 'large', 
  showText = false, 
  showLastSeen = false 
}) => {
  const userPresence = useSelector((state) => 
    state.chat.onlineUsers[userId] || { isOnline: false, status: 'offline' }
  );

  const getDotSize = () => {
    switch (size) {
      case 'small': return 8;
      case 'medium': return 10;
      case 'large': return 12;
      default: return 8;
    }
  };

  const getLastSeenText = () => {
    if (!userPresence.lastSeen) return 'Last seen: Unknown';
    try {
      const lastSeenDate = new Date(userPresence.lastSeen);
      return `Last seen: ${formatDistanceToNow(lastSeenDate, { addSuffix: true })}`;
    } catch (error) {
      return 'Last seen: Unknown';
    }
  };

  const dotSize = getDotSize();
  const isOnline = userPresence.isOnline;

  const statusDot = (
    <Box
      sx={{
        width: dotSize,
        height: dotSize,
        borderRadius: '50%',
        backgroundColor: isOnline ? '#10B981' : '#94A3B8',
        border: '2px solid #FFFFFF',
        animation: isOnline ? `${pulseEffect} 2s infinite` : 'none',
        transition: 'all 0.2s ease'
      }}
    />
  );

  if (showText) {
    return (
      <Tooltip title={isOnline ? 'Online' : getLastSeenText()} placement="top">
        <Box display="flex" alignItems="center" gap={1}>
          {statusDot}
          <Typography 
            sx={{ 
              fontSize: '12px', 
              fontWeight: isOnline ? 600 : 400,
              color: isOnline ? '#059669' : '#64748B',
              fontFamily: "'Inter', sans-serif"
            }}
          >
            {isOnline ? 'Online' : 'Offline'}
          </Typography>
          {showLastSeen && !isOnline && (
            <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontFamily: "'Inter', sans-serif" }}>
              • {getLastSeenText().replace('Last seen: ', '')}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={isOnline ? 'Online' : getLastSeenText()} placement="top">
      {statusDot}
    </Tooltip>
  );
};

export default OnlineIndicator;
